import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import {
  Button,
  Checkbox,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import BrandOverviewTableSkeleton from "../../../BrandSkeletons/BrandOverviewTableSkeleton";
import { Stack } from "@mui/system";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import SearchIcon from "@mui/icons-material/Search";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import useScrollToRef from "../../../../../../utils/useScrollToRef";

interface Props {
  tableHeadings?: { [key: string]: string };
  onFilteredDataChange?: (data: any[]) => void;
}

const LABEL_MAP: Record<string, string> = {
  id: "ID",
  rowid: "Row ID",
  currencysymbol: "Currency",
  totalrecords: "Total Records",
  merchantname: "Merchant Name",
  impressions: "Impressions",
  clicks: "Clicks",
  clickthroughratio: "CTR",
  downloads: "Downloads",
  downloadratio: "Download Ratio",
  newaccountratio: "New Account Ratio",
  newdepositingacc: "New Depositing Acc",
  newaccounts: "New Accounts",
  firstdepositcount: "First Deposit Count",
  activeaccounts: "Active Accounts",
  activedays: "Active Days",
  newpurchases: "New Purchases",
  purchaccountcount: "Purch Account Count",
  wageraccountcount: "Wager Account Count",
  avgactivedays: "Avg Active Days",
  netrevenueplayer: "Net Revenue / Player",
  Deposits: "Deposits",
  Netrevenue: "Net Revenue",
  stake: "Stake",
  "%Commission": "% Commission",
  CPACommission: "CPA Commission",
  cpacommissioncount: "CPA Commission Count",
  totalcommission: "Total Commission",
  brand_action_date: "Brand Action Date",
  created: "Created",
  does_exist: "Does Exist",
  Type: "Type",
  Brand: "Brand",
  Campaign: "Campaign",
  Date: "Date",
  Code: "Code",
  "Code Name": "Code Name",
  SubID: "SubID",
  OS: "OS",
  Clicks: "Clicks",
  Leads: "Leads",
  Reals: "Reals",
  Frauds: "Frauds",
  "Deposit#": "Deposit #",
  Withdrawals: "Withdrawals",
  Chargebacks: "Chargebacks",
  Net: "Net",
};

const DATE_KEYS = new Set([
  "registrationdate",
  "firstdeposit",
  "brand_action_date",
  "created",
]);

const CURRENCY_KEYS = new Set([
  "Deposits",
  "Withdrawals",
  "Net",
  "Netrevenue",
  "netrevenueplayer",
  "stake",
  "totalcommission",
  "CPACommission",
  "%Commission",
]);

// ── Per-column filter dropdown ─────────────────────────────────────────────
interface ColumnFilterProps {
  columnKey: string;
  label: string;
  allValues: string[];
  selectedValues: Set<string> | null;
  onChange: (key: string, values: Set<string> | null) => void;
  isActive: boolean;
}

const ITEM_HEIGHT = 36;
const MAX_VISIBLE_ITEMS = 8;

const ColumnFilter = ({
  columnKey,
  label,
  allValues,
  selectedValues,
  onChange,
  isActive,
}: ColumnFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState("");
  const [snapshotSelected, setSnapshotSelected] = useState<string[]>([]);
  const [snapshotUnselected, setSnapshotUnselected] = useState<string[]>([]);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setSearch("");
    const sel = allValues.filter(
      (v) => selectedValues !== null && selectedValues.has(v),
    );
    const unsel = allValues.filter(
      (v) => selectedValues === null || !selectedValues.has(v),
    );
    setSnapshotSelected(sel);
    setSnapshotUnselected(unsel);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleToggle = (value: string) => {
    const current = selectedValues ?? new Set<string>();
    const next = new Set(current);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    onChange(columnKey, next.size === 0 ? null : next);
  };

  const isChecked = (val: string) =>
    selectedValues !== null && selectedValues.has(val);

  const filteredUnselected = search.trim()
    ? snapshotUnselected.filter((v) =>
        v.toLowerCase().includes(search.trim().toLowerCase()),
      )
    : snapshotUnselected;

  // ── Flatten into a single list for react-window ──
  // Items: selected (pinned) + divider marker + filtered unselected
  type ListItem =
    | { kind: "item"; val: string; pinned: boolean }
    | { kind: "divider" };

  const listItems = useMemo<ListItem[]>(() => {
    const items: ListItem[] = [];
    snapshotSelected.forEach((val) =>
      items.push({ kind: "item", val, pinned: true }),
    );
    if (snapshotSelected.length > 0) {
      items.push({ kind: "divider" });
    }
    filteredUnselected.forEach((val) =>
      items.push({ kind: "item", val, pinned: false }),
    );
    return items;
  }, [snapshotSelected, filteredUnselected]);

  const listHeight =
    Math.min(listItems.length, MAX_VISIBLE_ITEMS) * ITEM_HEIGHT;

  // ── Row renderer for react-window ──
  const Row = useCallback(
    ({ index, style }: ListChildComponentProps) => {
      const item = listItems[index];

      if (item.kind === "divider") {
        return (
          <div style={style}>
            <Divider sx={{ borderStyle: "dashed" }} />
          </div>
        );
      }

      const { val, pinned } = item;
      return (
        <div style={style}>
          <MenuItem dense onClick={() => handleToggle(val)} sx={{ px: 1.5 }}>
            <Checkbox
              checked={isChecked(val)}
              size="small"
              sx={{
                p: 0,
                mr: 1,
                color: "#7B1FA2",
                "&.Mui-checked": { color: "#7B1FA2" },
              }}
            />
            <ListItemText
              primary={val === "" ? "(empty)" : val}
              primaryTypographyProps={{
                fontSize: "0.82rem",
                noWrap: true,
                fontWeight: pinned ? 500 : 400,
              }}
            />
          </MenuItem>
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listItems, selectedValues],
  );

  return (
    <>
      <Button
        size="small"
        variant={isActive ? "contained" : "outlined"}
        color={isActive ? "secondary" : "inherit"}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleOpen}
        sx={{
          fontSize: "0.72rem",
          textTransform: "none",
          fontWeight: isActive ? 600 : 400,
          whiteSpace: "nowrap",
          minWidth: 0,
          px: 1,
          py: 0.3,
          borderColor: isActive ? undefined : "divider",
          color: isActive ? undefined : "text.secondary",
        }}
      >
        {label}
        {isActive && selectedValues && selectedValues.size > 0
          ? ` +${selectedValues.size}`
          : ""}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 280,
            display: "flex",
            flexDirection: "column",
            // No maxHeight here — react-window controls the list height
          },
        }}
        MenuListProps={{
          sx: {
            p: 0,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden",
          },
        }}
      >
        {/* ── Search box ── */}
        <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
          <InputBase
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            startAdornment={
              <SearchIcon
                sx={{ fontSize: 18, mr: 0.5, color: "text.disabled" }}
              />
            }
            sx={{
              width: "100%",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              px: 1,
              py: 0.4,
              fontSize: "0.82rem",
            }}
          />
        </Box>

        {/* ── Heading ── */}
        <Typography
          variant="caption"
          sx={{
            px: 2,
            pb: 0.5,
            fontWeight: 600,
            color: "text.secondary",
            display: "block",
          }}
        >
          Select one or more {label}
        </Typography>

        <Divider />

        {/* ── Virtualized list ── */}
        {listItems.length > 0 ? (
          <FixedSizeList
            height={listHeight}
            itemCount={listItems.length}
            itemSize={ITEM_HEIGHT}
            width="100%"
            overscanCount={5} // render 5 extra rows above/below viewport
          >
            {Row}
          </FixedSizeList>
        ) : (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              py: 2,
              color: "text.disabled",
            }}
          >
            No options
          </Typography>
        )}
      </Menu>
    </>
  );
};

// ── Main table component ───────────────────────────────────────────────────
const RawTableDetails = ({ tableHeadings, onFilteredDataChange }: Props) => {
  const [sort, setSort] = useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "", direction: "desc" });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [columnFilters, setColumnFilters] = useState<
    Record<string, Set<string> | null>
  >({});

  // Scroll to table on rowsPerPage change, with default 80px offset
  const tableContainerRef = useScrollToRef([rowsPerPage], 150);

  const loaderForSwitchingTabs = useSelector(
    (state: any) => state.brandConfigurationReport.loaderForSwitchingTabs,
  );

  const rawTableData = useSelector(
    (state: any) => state.brandConfigurationReport.rawTableData,
  );

  // ── Dynamic column headers ─────────────────────────────────────────────
  const tableHeaders = useMemo(() => {
    let keys: string[] = [];
    if (tableHeadings && Object.keys(tableHeadings).length > 0) {
      keys = Object.keys(tableHeadings);
    } else if (rawTableData && rawTableData.length > 0) {
      keys = Object.keys(rawTableData[0]);
    }
    return keys.map((key, i) => ({
      id: i + 1,
      KEY: key,
      label: LABEL_MAP[key] ?? key,
      sortable: true,
    }));
  }, [tableHeadings, rawTableData]);

  // Initialize sort to first column; preserve user's sort when columns haven't changed
  useEffect(() => {
    if (tableHeaders.length > 0) {
      const keyExists = tableHeaders.some((h) => h.KEY === sort.keytoSort);
      if (!sort.keytoSort || !keyExists) {
        setSort({ keytoSort: tableHeaders[0].KEY, direction: "desc" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableHeaders]);

  // Reset page & filters when raw data changes
  useEffect(() => {
    setPage(0);
    setColumnFilters({});
  }, [rawTableData]);

  // ── Unique values per column ───────────────────────────────────────────
  const columnUniqueValues = useMemo<Record<string, string[]>>(() => {
    if (!rawTableData || rawTableData.length === 0) return {};

    const map: Record<string, Set<string>> = {};
    for (const row of rawTableData) {
      for (const { KEY } of tableHeaders) {
        if (!map[KEY]) map[KEY] = new Set();
        const val = row[KEY];
        map[KEY].add(val === null || val === undefined ? "" : String(val));
      }
    }

    const result: Record<string, string[]> = {};
    for (const [key, set] of Object.entries(map)) {
      const arr = Array.from(set);
      const allNumeric = arr.every((v) => v === "" || !isNaN(Number(v)));
      if (allNumeric) {
        arr.sort((a, b) => {
          if (a === "") return 1;
          if (b === "") return -1;
          return Number(a) - Number(b);
        });
      } else {
        arr.sort((a, b) => a.localeCompare(b));
      }
      result[key] = arr;
    }
    return result;
  }, [rawTableData, tableHeaders]);

  // ── Filter + sort (single synchronous pass — no flicker) ──────────────
  const sortedData = useMemo(() => {
    if (!rawTableData || rawTableData.length === 0) return [];

    const filtered = rawTableData.filter((row: any) =>
      tableHeaders.every(({ KEY }) => {
        const selected = columnFilters[KEY];
        if (selected === null || selected === undefined) return true;
        if (selected.size === 0) return false;
        const cellVal =
          row[KEY] === null || row[KEY] === undefined ? "" : String(row[KEY]);
        return selected.has(cellVal);
      }),
    );

    if (!sort.keytoSort || !sort.direction) return filtered;

    return filtered.slice().sort((a:any, b:any) => {
      const key = sort.keytoSort;
      const aValue = a[key];
      const bValue = b[key];

      if (aValue === undefined || aValue === "") return 1;
      if (bValue === undefined || bValue === "") return -1;

      if (DATE_KEYS.has(key) && typeof aValue === "string" && typeof bValue === "string") {
        const aMs = new Date(aValue).getTime();
        const bMs = new Date(bValue).getTime();
        if (!isNaN(aMs) && !isNaN(bMs)) {
          return sort.direction === "asc" ? aMs - bMs : bMs - aMs;
        }
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      return sort.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [rawTableData, sort, columnFilters, tableHeaders]);

  useEffect(() => {
    onFilteredDataChange?.(sortedData);
  }, [sortedData]); // eslint-disable-line react-hooks/exhaustive-deps

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleHeaderClick = (header: { KEY: string; sortable: boolean }) => {
    if (!header.sortable) return;
    setSort((prev) => ({
      keytoSort: header.KEY,
      direction:
        prev.keytoSort === header.KEY && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleFilterChange = (key: string, values: Set<string> | null) => {
    setColumnFilters((prev) => ({ ...prev, [key]: values }));
    setPage(0);
  };

  const isFilterActive = (key: string) =>
    columnFilters[key] !== undefined && columnFilters[key] !== null;

  const anyFilterActive = tableHeaders.some((h) => isFilterActive(h.KEY));

  const handleClearAllFilters = () => {
    setColumnFilters({});
    setPage(0);
  };

  // ── Cell renderer ──────────────────────────────────────────────────────
  const renderCell = (key: string, row: any) => {
    const value = row[key];
    if (value === null || value === undefined) return "";

    if (key === "does_exist") {
      return value === 1 || value === true ? "Yes" : "No";
    }
    if (key === "created" || key === "brand_action_date") {
      return value ? dayjs(value).format("DD/MM/YYYY, HH:mm:ss") : "";
    }
    if (CURRENCY_KEYS.has(key)) {
      const num = Number(value);
      return !isNaN(num) && value !== "" ? num.toFixed(2) : String(value);
    }
    return value !== "" ? String(value) : "";
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <>
      {loaderForSwitchingTabs ? (
        <BrandOverviewTableSkeleton />
      ) : (
        <>
          {/* ── Filter bar ── */}
          {tableHeaders.length > 0 && (
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              alignItems="center"
              mb={1.5}
              sx={{ overflowX: "auto", pb: 0.5 }}
            >
              {tableHeaders.map((header) => (
                <ColumnFilter
                  key={header.KEY}
                  columnKey={header.KEY}
                  label={header.label}
                  allValues={columnUniqueValues[header.KEY] ?? []}
                  selectedValues={columnFilters[header.KEY] ?? null}
                  onChange={handleFilterChange}
                  isActive={isFilterActive(header.KEY)}
                />
              ))}

              {anyFilterActive && (
                <Button
                  size="small"
                  variant="text"
                  color="secondary"
                  startIcon={<FilterListOffIcon />}
                  onClick={handleClearAllFilters}
                  sx={{
                    ml: "auto",
                    whiteSpace: "nowrap",
                    fontSize: "0.75rem",
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Stack>
          )}

          {/* ── Table ── */}
          <TableContainer
            //Attach the ref to the element we want to scroll to
            ref={tableContainerRef}
            component={Paper}
            className="performanceTable"
            sx={{
              padding: "0px 0px",
              boxShadow: "none",
              borderStartStartRadius: "0px",
              overflowX: "auto",
            }}
          >
            <Table aria-label="collapsible table" size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header) => (
                    <TableCell
                      key={header.id}
                      align="left"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      <TableSortLabel
                        active={sort.keytoSort === header.KEY}
                        direction={
                          sort.keytoSort === header.KEY ? sort.direction : "asc"
                        }
                        onClick={() => handleHeaderClick(header)}
                        disabled={!header.sortable}
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedRows?.length > 0 ? (
                  paginatedRows.map((row: any, rowIndex: number) => (
                    <React.Fragment key={rowIndex}>
                      <TableRow
                        sx={{
                          "& > *": {
                            borderBottom:
                              "1px solid rgba(224, 224, 224, 1) !important",
                          },
                        }}
                      >
                        {tableHeaders.map((header, cellIndex) => (
                          <TableCell
                            key={cellIndex}
                            align="left"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {renderCell(header.KEY, row)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={tableHeaders.length} align="center">
                      No data found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Pagination ── */}
          {sortedData?.length > 0 && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={sortedData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                    { display: "none" },
                  ".MuiTablePagination-actions": { display: "none" },
                }}
              />
              <Pagination
                count={Math.ceil(sortedData?.length / rowsPerPage)}
                page={page + 1}
                onChange={(_, value) => handleChangePage(null, value - 1)}
                showFirstButton
                showLastButton
                className="pagination-button"
                color="secondary"
                size="small"
                sx={{
                  ".css-wjh20t-MuiPagination-ul": { justifyContent: "center" },
                }}
              />
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={sortedData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                classes={{ displayedRows: "hidden-displayed-rows" }}
                sx={{
                  ".MuiTablePagination-actions": { display: "none" },
                  ".MuiTablePagination-input": { marginRight: "15px" },
                }}
              />
            </Stack>
          )}
        </>
      )}
    </>
  );
};

export default RawTableDetails;
